import { useState, useMemo, useCallback } from "react"

import { getYear } from "date-fns"
import { useNavigate, useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FilterListRoundedIcon } from "@l2r-front/l2r-icons"
import { RoadDetailInfo } from "@l2r-front/l2r-networks"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { RoadMultiYearGrading } from "../../../roadway/containers/RoadMultiYearGrading"
import { FILTERS } from "../../constants/filters"
import { useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { useRoadworkCreate } from "../../hooks/mutations/roadwork/useRoadworkCreate"

import { RoadRoadworksList } from "../RoadRoadworksList"
import { RoadworkSectionPainter } from "../RoadworkSectionPainter"
import { TechniquesFilterList } from "../TechniquesFilterList"

import * as Styled from "./RoadworksRoadSidebarContent.styled"

export function RoadworksRoadSidebarContent(props) {

    const { className } = props

    const [displayFilters, setDisplayFilters] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const isMobile = useIsMobileDevice()
    const { t } = useTranslation(I18N_NAMESPACE)
    const { road } = useParams()
    const navigate = useNavigate()
    const { project } = useRoadworksStateContext()
    const [mutationRunning, setMutationRunning] = useState(false)

    const mutation = useRoadworkCreate({}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkDetail.snackbar.errorCreate"),
        onSuccess: (newRoadwork) => navigate(newRoadwork?.uuid),
        onError: () => {
            setMutationRunning(false)
            closeModal()
        },
    })

    const closeFilters = useCallback(() => setDisplayFilters(false), [])
    const toggleFilter = useCallback(() => setDisplayFilters(displayFilters => !displayFilters), [])
    const closeModal = useCallback(() => setModalOpen(false), [])

    const createRoadwork = (data) => {
        const newRoadwork = {
            ...data,
            project: project?.uuid,
            year: getYear(new Date()),
        }
        mutation.mutate(newRoadwork)
        setMutationRunning(true)
    }

    const DesktopFilterComponent = useMemo(() => <Styled.DisplayedFiltersWrapper>
        <TechniquesFilterList enabledFilters={FILTERS} />
        <Styled.IconButton onClick={toggleFilter} id="filters-button">
            <FilterListRoundedIcon color="primary" />
        </Styled.IconButton>
    </Styled.DisplayedFiltersWrapper>, [])

    return (
        <Styled.Container className={className} id="sidebar-content-container">
            <Styled.BackdropFilter
                id="backdrop-filters"
                onClick={toggleFilter}
                open={displayFilters}
            />
            <Styled.Header isMobile={isMobile}>
                <RoadMultiYearGrading />
                <RoadDetailInfo road={road} />
            </Styled.Header>
            <Styled.TechniquesContainer isMobile={isMobile}>
                {!isMobile && DesktopFilterComponent}
                {!isMobile && <Styled.Wrapper>
                    <Styled.TechniquesFilters enabledFilters={FILTERS} close={closeFilters} displayFilters={displayFilters} />
                </Styled.Wrapper>}
                <RoadRoadworksList openDrawModal={() => setModalOpen(true)} />
            </Styled.TechniquesContainer>
            {modalOpen && <RoadworkSectionPainter
                open={modalOpen}
                onClose={closeModal}
                onSave={createRoadwork}
                showLoader={mutationRunning}
                title={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.create.title")}
                desc={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.create.desc")}
                cancelButtonLabel={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.create.cancel")}
                saveButtonLabel={t(I18N_NAMESPACE, `components.roadworkSectionPainterModal.create.${isMobile ? "saveMobile" : "save"}`)}
            />}
        </Styled.Container>
    )
}